// src/components/NotificationSystem.jsx

import React, { useState, useEffect } from 'react';

// CSS for notifications
const notificationStyles = {
  container: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 1000,
    maxWidth: '350px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  notification: {
    padding: '12px 16px',
    borderRadius: '8px',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    animation: 'slideIn 0.3s ease-out forwards',
    transition: 'all 0.3s ease',
    opacity: 1,
    transform: 'translateX(0)'
  },
  success: {
    backgroundColor: 'rgba(16, 185, 129, 0.2)',
    borderColor: 'rgba(16, 185, 129, 0.3)'
  },
  error: {
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    borderColor: 'rgba(239, 68, 68, 0.3)'
  },
  info: {
    backgroundColor: 'rgba(99, 102, 241, 0.2)',
    borderColor: 'rgba(99, 102, 241, 0.3)'
  },
  warning: {
    backgroundColor: 'rgba(249, 115, 22, 0.2)',
    borderColor: 'rgba(249, 115, 22, 0.3)'
  },
  content: {
    flex: 1,
    marginRight: '10px'
  },
  title: {
    fontWeight: 600,
    fontSize: '16px',
    marginBottom: '5px'
  },
  message: {
    fontSize: '14px'
  },
  closeButton: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255, 255, 255, 0.6)',
    cursor: 'pointer',
    fontSize: '18px',
    padding: '0',
    transition: 'color 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px'
  },
  progress: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    height: '3px',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    transition: 'width linear'
  },
  exiting: {
    opacity: 0,
    transform: 'translateX(20px)'
  },
  icon: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  '@keyframes slideIn': {
    '0%': {
      opacity: 0,
      transform: 'translateX(20px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateX(0)'
    }
  }
};

/**
 * NotificationSystem Component
 * Provides a system to display notifications
 * @returns {JSX.Element}
 */
const NotificationSystem = () => {
  const [notifications, setNotifications] = useState([]);
  
  useEffect(() => {
    // Add the notification API to the window object
    window.notifications = {
      show: showNotification,
      success: (message, options = {}) => showNotification(message, { ...options, type: 'success' }),
      error: (message, options = {}) => showNotification(message, { ...options, type: 'error' }),
      info: (message, options = {}) => showNotification(message, { ...options, type: 'info' }),
      warning: (message, options = {}) => showNotification(message, { ...options, type: 'warning' }),
      clear: () => setNotifications([])
    };
    
    // Cleanup function
    return () => {
      delete window.notifications;
    };
  }, []);
  
  /**
   * Show a notification
   * @param {string} message - The notification message
   * @param {Object} options - Notification options
   * @param {string} options.type - Type of notification: 'success', 'error', 'info', 'warning'
   * @param {string} options.title - Title of the notification
   * @param {number} options.duration - Duration in ms before auto-dismissing (0 for no auto-dismiss)
   * @param {boolean} options.dismissible - Whether the notification can be dismissed manually
   */
  const showNotification = (message, options = {}) => {
    const {
      type = 'info',
      title = type.charAt(0).toUpperCase() + type.slice(1),
      duration = 5000,
      dismissible = true
    } = options;
    
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 5);
    
    const newNotification = {
      id,
      message,
      type,
      title,
      duration,
      dismissible,
      exiting: false
    };
    
    setNotifications(prev => [...prev, newNotification]);
    
    // Auto-dismiss after duration (if duration > 0)
    if (duration > 0) {
      setTimeout(() => {
        dismissNotification(id);
      }, duration);
    }
  };
  
  /**
   * Start dismissing a notification (with animation)
   * @param {string} id - Notification ID to dismiss
   */
  const dismissNotification = (id) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, exiting: true } 
          : notification
      )
    );
    
    // Remove after animation completes
    setTimeout(() => {
      setNotifications(prev => 
        prev.filter(notification => notification.id !== id)
      );
    }, 300); // Animation duration
  };
  
  /**
   * Render the progress bar for auto-dismissing notifications
   * @param {Object} notification - The notification object
   * @returns {JSX.Element|null}
   */
  const renderProgressBar = (notification) => {
    if (notification.duration <= 0) return null;
    
    return (
      <div 
        style={{
          ...notificationStyles.progress,
          width: '100%',
          animation: `shrink ${notification.duration}ms linear forwards`
        }}
      />
    );
  };
  
  /**
   * Render the icon for a notification based on its type
   * @param {string} type - Notification type
   * @returns {JSX.Element}
   */
  const renderIcon = (type) => {
    switch (type) {
      case 'success':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 4L12 14.01L9 11.01" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'error':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
            <path d="M12 8V12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 16H12.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
      case 'warning':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.29 3.86L1.82 18C1.64 18.32 1.49 18.68 1.38 19.06C1.27 19.43 1.22 19.82 1.22 20.21C1.22 20.6 1.27 20.99 1.38 21.36C1.49 21.74 1.64 22.1 1.82 22.42C2.01 22.74 2.23 23.02 2.5 23.25C2.76 23.48 3.06 23.67 3.38 23.8C3.71 23.93 4.05 24 4.41 24C4.77 24 5.11 23.93 5.44 23.8C5.76 23.67 6.07 23.48 6.33 23.25L23 3.86C23.27 3.63 23.49 3.35 23.68 3.03C23.86 2.71 24 2.35 24.06 1.98C24.13 1.6 24.15 1.21 24.1 0.82C24.05 0.43 23.95 0.05 23.8 -0.3C23.65 -0.65 23.45 -0.97 23.2 -1.24C22.96 -1.5 22.67 -1.72 22.35 -1.88C22.03 -2.03 21.68 -2.13 21.32 -2.18C20.96 -2.22 20.59 -2.21 20.23 -2.14C19.86 -2.08 19.5 -1.96 19.17 -1.78L10.29 3.86Z" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 9V13" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 17H12.01" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'info':
      default:
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#6366f1" strokeWidth="2"/>
            <path d="M12 16V12" stroke="#6366f1" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 8H12.01" stroke="#6366f1" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
    }
  };
  
  if (notifications.length === 0) {
    return null;
  }
  
  return (
    <div style={notificationStyles.container}>
      {notifications.map(notification => (
        <div
          key={notification.id}
          style={{
            ...notificationStyles.notification,
            ...(notification.exiting ? notificationStyles.exiting : {}),
            ...(notification.type ? notificationStyles[notification.type] : {})
          }}
        >
          <div style={notificationStyles.icon}>
            {renderIcon(notification.type)}
          </div>
          
          <div style={notificationStyles.content}>
            <div style={notificationStyles.title}>{notification.title}</div>
            <div style={notificationStyles.message}>{notification.message}</div>
          </div>
          
          {notification.dismissible && (
            <button
              style={notificationStyles.closeButton}
              onClick={() => dismissNotification(notification.id)}
              aria-label="Close notification"
            >
              ×
            </button>
          )}
          
          {renderProgressBar(notification)}
        </div>
      ))}
      
      <style>
        {`
          @keyframes shrink {
            from { width: 100%; }
            to { width: 0%; }
          }
          
          @keyframes slideIn {
            0% { opacity: 0; transform: translateX(20px); }
            100% { opacity: 1; transform: translateX(0); }
          }
        `}
      </style>
    </div>
  );
};

export default NotificationSystem;
